import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { notification } from "antd";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheck,
  FaCopy,
  FaSearch,
  FaSmile,
  FaSyncAlt,
  FaTimes,
  FaUserFriends,
  FaUsers,
} from "react-icons/fa";
import {
  getLocaleForLanguage,
  normalizeLanguage,
  translatePair,
} from "../../services/language";
import { setLanguage as setI18nLanguage, t as tKey } from "../../i18n";

const MODE_OPTIONS = [
  { value: "dice", es: "Dice", en: "Dice", ru: "Dice", de: "Dice" },
  { value: "cienzar", es: "Cienzar", en: "Cienzar", ru: "Cienzar", de: "Cienzar" },
  {
    value: "sciencevsazar",
    es: "Ciencia V/S Azar",
    en: "Science V/S Chance",
    ru: "Science V/S Chance",
    de: "Wissenschaft V/S Zufall",
  },
  { value: "free", es: "Free", en: "Free", ru: "Free", de: "Free" },
];

const TIME_OPTIONS = [3, 5, 10, 15, 30, 60];
const EMPTY_REQUESTS = { incoming: [], outgoing: [] };
const EMPTY_INVITES = { incoming: [], outgoing: [] };
const PREVIEW_SOCIAL_ITEMS = 4;

const DEFAULT_INVITE_DRAFT = {
  mode: "dice",
  havingTime: 5,
};

const cx = (...classes) => classes.filter(Boolean).join(" ");

const AVATAR_THEMES = [
  {
    ring: "border-[#19df78]",
    glow: "shadow-[0_0_22px_rgba(25,223,120,0.35)]",
    bg: "from-[#123d2e] via-[#075f3a] to-[#02130d]",
    icon: "text-[#7bffad]",
  },
  {
    ring: "border-[#ff9a24]",
    glow: "shadow-[0_0_22px_rgba(255,154,36,0.35)]",
    bg: "from-[#4d2102] via-[#b44c00] to-[#140700]",
    icon: "text-[#ffc46d]",
  },
  {
    ring: "border-[#c649ff]",
    glow: "shadow-[0_0_22px_rgba(198,73,255,0.35)]",
    bg: "from-[#271044] via-[#6f1ba7] to-[#0e0618]",
    icon: "text-[#e3a3ff]",
  },
  {
    ring: "border-[#19aeff]",
    glow: "shadow-[0_0_22px_rgba(25,174,255,0.35)]",
    bg: "from-[#062846] via-[#075da8] to-[#020b17]",
    icon: "text-[#7ad5ff]",
  },
];

const getSeedFromUser = (userLike = {}) => {
  const seed = String(
    userLike?.playerCode ||
      userLike?.displayName ||
      userLike?.email ||
      userLike?.id ||
      ""
  );
  return seed.split("").reduce((total, char) => total + char.charCodeAt(0), 0);
};

const PlayerAvatar = ({ user, className = "" }) => {
  const theme = AVATAR_THEMES[getSeedFromUser(user) % AVATAR_THEMES.length];
  return (
    <div
      className={cx(
        "flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2 bg-gradient-to-br",
        theme.ring,
        theme.bg,
        theme.glow,
        className
      )}
      aria-hidden="true"
    >
      <FaSmile className={cx("h-7 w-7", theme.icon)} />
    </div>
  );
};

const CountBadge = ({ children, tone = "blue" }) => {
  const toneClass =
    tone === "green"
      ? "bg-[#053d2f] text-[#49ffad]"
      : tone === "red"
        ? "bg-[#481019] text-[#ff5d73]"
        : tone === "yellow"
          ? "bg-[#4b3500] text-[#ffc738]"
          : "bg-[#0d3768] text-[#49b8ff]";

  return (
    <span
      className={cx(
        "inline-flex min-w-8 items-center justify-center rounded-md px-2 py-1 text-xs font-black",
        toneClass
      )}
    >
      {children}
    </span>
  );
};

const formatRelativeTime = (value, language) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "";

  const diffMs = date.valueOf() - Date.now();
  const absMs = Math.abs(diffMs);
  const units = [
    ["year", 365 * 24 * 60 * 60 * 1000],
    ["month", 30 * 24 * 60 * 60 * 1000],
    ["day", 24 * 60 * 60 * 1000],
    ["hour", 60 * 60 * 1000],
    ["minute", 60 * 1000],
  ];
  const [unit, unitMs] =
    units.find(([, duration]) => absMs >= duration) || ["second", 1000];
  const amount = Math.round(diffMs / unitMs);

  try {
    return new Intl.RelativeTimeFormat(getLocaleForLanguage(language), {
      numeric: "auto",
    }).format(amount, unit);
  } catch {
    return formatDate(value, language);
  }
};

const formatDate = (value, language) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "-";
  return date.toLocaleString(getLocaleForLanguage(language), {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizePlayerCode = (value) => {
  const raw = String(value || "")
    .trim()
    .toUpperCase();
  if (!raw) return "";
  if (/^\d{4}$/.test(raw)) return `XIMA-${raw}`;
  return raw;
};

const buildProfileTargetFromUser = (userLike = {}) => {
  const playerCode = normalizePlayerCode(userLike?.playerCode);
  if (playerCode) return playerCode;

  const loginIdentifier = String(
    userLike?.loginIdentifier || userLike?.email || ""
  ).trim();
  if (loginIdentifier) return loginIdentifier;

  return String(userLike?.id || userLike?._id || userLike?.userId || "").trim();
};

const Friends = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const outletContext = useOutletContext() || {};
  const language = normalizeLanguage(outletContext.language);
  const refreshSocialAlert = outletContext.refreshSocialAlert;
  const t = useCallback((es, en) => translatePair(language, es, en), [language]);
  useEffect(() => {
    setI18nLanguage(language);
  }, [language]);
  const outgoingInviteStateRef = useRef(new Map());
  const recentOutgoingInviteIdsRef = useRef(new Set());

  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchPlayerCode, setSearchPlayerCode] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [requests, setRequests] = useState(EMPTY_REQUESTS);
  const [friends, setFriends] = useState([]);
  const [invites, setInvites] = useState(EMPTY_INVITES);
  const [inviteDraftByFriendCode, setInviteDraftByFriendCode] = useState({});
  const [refreshTick, setRefreshTick] = useState(0);
  const [showAllFriends, setShowAllFriends] = useState(false);
  const [showAllIncomingInvites, setShowAllIncomingInvites] = useState(false);
  const [showAllOutgoingInvites, setShowAllOutgoingInvites] = useState(false);
  const fromLobbySource = useMemo(
    () => String(new URLSearchParams(location.search).get("fromLobbySource") || "").toLowerCase(),
    [location.search]
  );
  const friendsReturnRoute = fromLobbySource === "friends" ? "/game/lobby" : "/game";

  const relationLabel = useMemo(
    () => ({
      self: t("Eres tú", "This is you"),
      none: t("Sin relación", "No relation"),
      friend: t("Amigo", "Friend"),
      incoming_pending: t("Solicitud recibida", "Incoming request"),
      outgoing_pending: t("Solicitud enviada", "Request sent"),
    }),
    [t]
  );

  const statusLabel = useMemo(
    () => ({
      online: t("En línea", "Online"),
      in_match: t("En partida", "In match"),
      offline: t("Desconectado", "Offline"),
    }),
    [t]
  );

  const statusClass = useMemo(
    () => ({
      online: "border-[#00d877] bg-[#053a2d] text-[#67ffb2]",
      in_match: "border-[#22b9ff] bg-[#06345a] text-[#9de7ff]",
      offline: "border-[#1f365e] bg-[#12243e] text-[#d8e8ff]",
    }),
    []
  );

  const socialCounts = useMemo(() => {
    const incomingRequests = requests.incoming.length;
    const outgoingRequests = requests.outgoing.length;
    const incomingInvites = invites.incoming.length;
    const outgoingInvites = invites.outgoing.length;
    const allItems =
      friends.length + incomingRequests + outgoingRequests + incomingInvites + outgoingInvites;

    return {
      allItems,
      requestTotal: incomingRequests + outgoingRequests,
      inviteTotal: incomingInvites + outgoingInvites,
      connectedFriends: friends.filter((item) => item.status === "online").length,
      incomingRequests,
      outgoingRequests,
      incomingInvites,
      outgoingInvites,
    };
  }, [friends, invites.incoming, invites.outgoing, requests.incoming, requests.outgoing]);

  const visibleFriends = showAllFriends
    ? friends
    : friends.slice(0, PREVIEW_SOCIAL_ITEMS);
  const visibleIncomingInvites = showAllIncomingInvites
    ? invites.incoming
    : invites.incoming.slice(0, 1);
  const visibleOutgoingInvites = showAllOutgoingInvites
    ? invites.outgoing
    : invites.outgoing.slice(0, 1);

  const refreshData = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const [meResponse, requestsResponse, friendsResponse, invitesResponse] =
          await Promise.all([
            axios.get("/friends/me"),
            axios.get("/friends/requests"),
            axios.get("/friends/list"),
            axios.get("/friends/invites"),
          ]);

        setMe(meResponse?.data?.user || null);
        setRequests({
          incoming: Array.isArray(requestsResponse?.data?.incoming)
            ? requestsResponse.data.incoming
            : [],
          outgoing: Array.isArray(requestsResponse?.data?.outgoing)
            ? requestsResponse.data.outgoing
            : [],
        });
        setFriends(
          Array.isArray(friendsResponse?.data?.friends)
            ? friendsResponse.data.friends
            : []
        );
        setInvites({
          incoming: Array.isArray(invitesResponse?.data?.incoming)
            ? invitesResponse.data.incoming
            : [],
          outgoing: Array.isArray(invitesResponse?.data?.outgoing)
            ? invitesResponse.data.outgoing
            : [],
        });
      } catch (error) {
        if (!silent) {
          notification.error({
            message: t("No se pudo cargar Amigos", "Could not load Friends"),
            description:
              error?.response?.data?.message ||
              t("Revisa que backend esté activo.", "Check that backend is running."),
          });
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [t]
  );

  const triggerSocialRefresh = useCallback(() => {
    if (typeof refreshSocialAlert === "function") {
      refreshSocialAlert().catch((error) => console.log(error));
    }
  }, [refreshSocialAlert]);

  useEffect(() => {
    refreshData(false);
  }, [refreshData, refreshTick]);

  useEffect(() => {
    const hasOutgoingPending = invites.outgoing.some(
      (invite) => String(invite?.status || "").toLowerCase() === "pending"
    );
    const intervalMs = hasOutgoingPending ? 3000 : 10000;
    const intervalId = setInterval(() => {
      refreshData(true);
    }, intervalMs);
    return () => clearInterval(intervalId);
  }, [invites.outgoing, refreshData]);

  useEffect(() => {
    let acceptedForAutoJoin = null;
    const nextStateMap = new Map();

    invites.outgoing.forEach((invite) => {
      const inviteId = String(invite?.id || "").trim();
      const status = String(invite?.status || "")
        .trim()
        .toLowerCase();
      const tournamentId = String(invite?.tournamentId || "").trim();
      if (!inviteId) return;

      nextStateMap.set(inviteId, { status, tournamentId });
      if (acceptedForAutoJoin) return;
      if (status !== "accepted" || !tournamentId) return;

      const previous = outgoingInviteStateRef.current.get(inviteId);
      const changedFromPending = previous?.status === "pending";
      const isRecentSentInvite = recentOutgoingInviteIdsRef.current.has(inviteId);
      if (changedFromPending || isRecentSentInvite) {
        acceptedForAutoJoin = { inviteId, tournamentId };
      }
    });

    outgoingInviteStateRef.current = nextStateMap;

    if (!acceptedForAutoJoin) return;

    recentOutgoingInviteIdsRef.current.delete(acceptedForAutoJoin.inviteId);
    notification.success({
      message: t("Tu amigo aceptó la invitación", "Your friend accepted the invite"),
      description: t("Entrando a la partida...", "Entering match..."),
    });
    navigate(`/game/${acceptedForAutoJoin.tournamentId}`);
  }, [invites.outgoing, navigate, t]);

  const getInviteDraft = (friendPlayerCode) =>
    inviteDraftByFriendCode[friendPlayerCode] || DEFAULT_INVITE_DRAFT;

  const setInviteDraftField = (friendPlayerCode, key, value) => {
    setInviteDraftByFriendCode((prev) => ({
      ...prev,
      [friendPlayerCode]: {
        ...(prev[friendPlayerCode] || DEFAULT_INVITE_DRAFT),
        [key]: value,
      },
    }));
  };

  const handleSearch = async () => {
    const normalizedCode = normalizePlayerCode(searchPlayerCode);
    if (!normalizedCode) {
      notification.warning({
        message: t("Código requerido", "Code required"),
        description: t(
          "Escribe el código del jugador para buscar.",
          "Enter the player's code to search."
        ),
      });
      return;
    }

    setSearchLoading(true);
    try {
      const response = await axios.get("/friends/search", {
        params: { playerCode: normalizedCode },
      });
      setSearchResult(response?.data || null);
    } catch (error) {
      setSearchResult(null);
      notification.error({
        message: t("No encontrado", "Not found"),
        description:
          error?.response?.data?.message ||
          t("No existe un jugador con ese código.", "No player exists with that code."),
      });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSendRequest = async (targetPlayerCode) => {
    try {
      await axios.post("/friends/request", { targetPlayerCode });
      notification.success({
        message: t("Solicitud enviada", "Request sent"),
        description: t(
          "La solicitud de amistad fue enviada.",
          "Friend request was sent."
        ),
      });
      setSearchResult((prev) =>
        prev
          ? {
              ...prev,
              relation: "outgoing_pending",
            }
          : prev
      );
      setRefreshTick((prev) => prev + 1);
      triggerSocialRefresh();
    } catch (error) {
      notification.error({
        message: t("No se pudo enviar", "Could not send request"),
        description:
          error?.response?.data?.message ||
          t("Intenta de nuevo.", "Try again."),
      });
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await axios.post(`/friends/request/${requestId}/accept`);
      notification.success({
        message: t("Solicitud aceptada", "Request accepted"),
        description: t("Ya son amigos.", "You are now friends."),
      });
      setRefreshTick((prev) => prev + 1);
      triggerSocialRefresh();
    } catch (error) {
      notification.error({
        message: t("No se pudo aceptar", "Could not accept"),
        description:
          error?.response?.data?.message ||
          t("Intenta de nuevo.", "Try again."),
      });
    }
  };

  const handleDenyRequest = async (requestId) => {
    try {
      await axios.post(`/friends/request/${requestId}/deny`);
      notification.info({
        message: t("Solicitud denegada", "Request denied"),
        description: t("La solicitud fue rechazada.", "The request was rejected."),
      });
      setRefreshTick((prev) => prev + 1);
      triggerSocialRefresh();
    } catch (error) {
      notification.error({
        message: t("No se pudo denegar", "Could not deny"),
        description:
          error?.response?.data?.message ||
          t("Intenta de nuevo.", "Try again."),
      });
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      await axios.post(`/friends/request/${requestId}/cancel`);
      notification.info({
        message: t("Solicitud cancelada", "Request canceled"),
        description: t("Se removió la solicitud pendiente.", "Pending request was removed."),
      });
      setRefreshTick((prev) => prev + 1);
      triggerSocialRefresh();
    } catch (error) {
      notification.error({
        message: t("No se pudo cancelar", "Could not cancel"),
        description:
          error?.response?.data?.message ||
          t("Intenta de nuevo.", "Try again."),
      });
    }
  };

  const handleSendInvite = async (friendPlayerCode) => {
    const draft = getInviteDraft(friendPlayerCode);
    try {
      const response = await axios.post("/friends/invite", {
        friendPlayerCode,
        mode: draft.mode,
        havingTime: Number(draft.havingTime),
      });
      const inviteId = String(response?.data?.invite?.id || "").trim();
      if (inviteId) {
        recentOutgoingInviteIdsRef.current.add(inviteId);
      }
      notification.success({
        message: t("Invitación enviada", "Invite sent"),
        description: t(
          "La invitación de partida fue enviada.",
          "Game invite was sent."
        ),
      });
      setRefreshTick((prev) => prev + 1);
      triggerSocialRefresh();
    } catch (error) {
      notification.error({
        message: t("No se pudo invitar", "Could not invite"),
        description:
          error?.response?.data?.message ||
          t("Intenta de nuevo.", "Try again."),
      });
    }
  };

  const handleJoinFriend = async (friendPlayerCode) => {
    try {
      const response = await axios.get("/friends/join", {
        params: { friendPlayerCode },
      });

      const tournamentId = String(response?.data?.tournamentId || "").trim();
      const roomId = String(response?.data?.roomId || "").trim();
      if (!tournamentId || !roomId) {
        throw new Error(t("No hay sala activa para unirse.", "No active room to join."));
      }

      navigate(
        `/game/${encodeURIComponent(tournamentId)}?autoplay=1&spectator=1&roomId=${encodeURIComponent(
          roomId
        )}`
      );
    } catch (error) {
      notification.error({
        message: t("No se pudo unir", "Could not join"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          t("La sala del amigo no está disponible.", "Friend room is not available."),
      });
    }
  };

  const handleAcceptInvite = async (inviteId) => {
    try {
      const response = await axios.post(`/friends/invite/${inviteId}/accept`);
      const tournamentId = String(response?.data?.tournamentId || "").trim();
      notification.success({
        message: t("Invitación aceptada", "Invite accepted"),
        description: t(
          "La sala fue creada. Entrando a la partida...",
          "Room created. Entering match..."
        ),
      });
      setRefreshTick((prev) => prev + 1);
      triggerSocialRefresh();
      if (tournamentId) {
        navigate(`/game/${tournamentId}`);
      }
    } catch (error) {
      notification.error({
        message: t("No se pudo aceptar", "Could not accept"),
        description:
          error?.response?.data?.message ||
          t("Intenta de nuevo.", "Try again."),
      });
    }
  };

  const handleDenyInvite = async (inviteId) => {
    try {
      await axios.post(`/friends/invite/${inviteId}/deny`);
      notification.info({
        message: t("Invitación rechazada", "Invite denied"),
        description: t("La invitación fue rechazada.", "Invite was declined."),
      });
      setRefreshTick((prev) => prev + 1);
      triggerSocialRefresh();
    } catch (error) {
      notification.error({
        message: t("No se pudo rechazar", "Could not decline"),
        description:
          error?.response?.data?.message ||
          t("Intenta de nuevo.", "Try again."),
      });
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await axios.post(`/friends/invite/${inviteId}/cancel`);
      notification.info({
        message: t("Invitación cancelada", "Invite canceled"),
        description: t("La invitación pendiente fue cancelada.", "Pending invite was canceled."),
      });
      setRefreshTick((prev) => prev + 1);
      triggerSocialRefresh();
    } catch (error) {
      notification.error({
        message: t("No se pudo cancelar", "Could not cancel"),
        description:
          error?.response?.data?.message ||
          t("Intenta de nuevo.", "Try again."),
      });
    }
  };

  const copyMyPlayerCode = async () => {
    const playerCode = String(me?.playerCode || "").trim();
    if (!playerCode) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(playerCode);
        notification.success({
          message: t("Código copiado", "Code copied"),
          description: playerCode,
        });
      }
    } catch {
      notification.info({
        message: t("Copia manual", "Manual copy"),
        description: playerCode,
      });
    }
  };
  const openPlayerProfile = useCallback(
    (userLike) => {
      const target = buildProfileTargetFromUser(userLike);
      if (!target) {
        notification.warning({
          message: t("Perfil no disponible", "Profile unavailable"),
          description: t(
            "No se encontró un identificador de jugador para abrir el perfil.",
            "No player identifier was found to open this profile."
          ),
        });
        return;
      }
      navigate(`/game/player/${encodeURIComponent(target)}`);
    },
    [navigate, t]
  );

  return (
    <div className="scrollbar-gamified h-full min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain bg-[#010613] bg-[radial-gradient(circle_at_18%_0%,rgba(31,139,255,0.22),transparent_28%),radial-gradient(circle_at_86%_10%,rgba(0,210,120,0.12),transparent_24%),linear-gradient(180deg,#020616_0%,#020a1c_52%,#020714_100%)] px-4 py-5 pb-[180px] text-white md:px-6 md:pb-[220px]">
      <div className="mx-auto flex w-full max-w-[1680px] flex-col gap-4">
        <section className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-center gap-5">
            <div className="flex h-[62px] w-[62px] shrink-0 items-center justify-center rounded-md border border-[#118cff] bg-[#06173b] shadow-[0_0_30px_rgba(31,139,255,0.38)]">
              <FaUserFriends className="h-9 w-9 text-[#39b7ff]" />
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-3xl font-black uppercase text-white drop-shadow-[0_0_12px_rgba(70,160,255,0.95)] md:text-4xl">
                {tKey("AMIGOS")}
              </h1>
              <p className="mt-1 text-sm font-medium text-[#c7dbf5] md:text-base">
                {t(
                  "Juega con amigos, acepta solicitudes e invita a otros a tu partida.",
                  "Play with friends, accept requests, and invite others to your match."
                )}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={() => setRefreshTick((prev) => prev + 1)}
              className="inline-flex min-h-[46px] items-center justify-center gap-3 rounded-md border border-[#167dff] bg-[#041938] px-6 text-sm font-black uppercase text-[#4db7ff] shadow-[0_0_18px_rgba(22,125,255,0.12)] transition hover:bg-[#072653] disabled:cursor-not-allowed disabled:opacity-60"
              disabled={loading}
            >
              <span>{tKey("ACTUALIZAR")}</span>
              <FaSyncAlt className={cx("h-4 w-4", loading ? "animate-spin" : "")} />
            </button>
            <button
              type="button"
              onClick={() => navigate(friendsReturnRoute)}
              className="inline-flex min-h-[46px] items-center justify-center gap-3 rounded-md border border-[#00c978] bg-[#031d18] px-6 text-sm font-black uppercase text-[#69ffbb] shadow-[0_0_18px_rgba(0,201,120,0.12)] transition hover:bg-[#063429]"
            >
              <FaArrowLeft className="h-4 w-4" />
              <span>{tKey("VOLVER_AL_LOBBY")}</span>
            </button>
          </div>
        </section>

        <section className="grid gap-4 xl:grid-cols-[1.02fr_1fr]">
          <article className="rounded-md border border-[#ffbf1f] bg-[#1c1302]/95 p-5 shadow-[0_0_26px_rgba(255,191,31,0.18)]">
            <div className="flex items-start gap-3">
              <FaSearch className="mt-1 h-5 w-5 text-[#ffc83d]" />
              <div>
                <h2 className="text-lg font-black uppercase text-[#fff5c7]">
                  {tKey("BUSCAR_JUGADOR_POR_CODIGO")}
                </h2>
                <p className="mt-1 text-sm font-medium text-[#e7c979]">
                  {t("Busca jugadores por su codigo unico.", "Find players by their unique code.")}
                </p>
              </div>
            </div>

            <div className="mt-6">
              <label className="text-xs font-black uppercase text-[#fff0ad]">
                {t("Tu codigo de jugador", "Your player code")}
              </label>
              <div className="mt-2 grid gap-3 sm:grid-cols-[1fr_auto]">
                <div className="flex min-h-[48px] items-center rounded-md border border-[#8b6514] bg-[#120c02] px-4 text-base font-black text-[#fff7d1] shadow-[inset_0_0_18px_rgba(255,191,31,0.12)]">
                  <span className="break-all">{me?.playerCode || "-"}</span>
                </div>
                <button
                  type="button"
                  onClick={copyMyPlayerCode}
                  disabled={!String(me?.playerCode || "").trim()}
                  className="inline-flex min-h-[48px] min-w-[178px] items-center justify-center gap-3 rounded-md border border-[#b68419] bg-[#261902] px-5 text-xs font-black uppercase text-[#fff2b6] transition hover:border-[#ffd45c] hover:bg-[#342403] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span>{tKey("COPIAR_CODIGO")}</span>
                  <FaCopy className="h-4 w-4 text-[#ffd45c]" />
                </button>
              </div>
            </div>

            <div className="mt-6">
              <label className="text-xs font-black uppercase text-[#fff0ad]">
                {t("Ingresa codigo de jugador", "Enter player code")}
              </label>
              <div className="mt-2 grid gap-3 sm:grid-cols-[1fr_auto]">
                <input
                  value={searchPlayerCode}
                  onChange={(event) => setSearchPlayerCode(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  placeholder={t("Ej: XIMA-4K2", "Ex: XIMA-4K2")}
                  className="min-h-[48px] rounded-md border border-[#8b6514] bg-[#120c02] px-4 text-sm font-semibold text-[#fff7d1] outline-none transition placeholder:text-[#8e7a45] focus:border-[#ffd45c] focus:shadow-[0_0_0_2px_rgba(255,191,31,0.22)]"
                />
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={searchLoading}
                  className="min-h-[48px] min-w-[130px] rounded-md border border-[#ffbf1f] bg-[#241802] px-5 text-sm font-black uppercase text-[#ffd45c] transition hover:bg-[#372503] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {searchLoading ? t("Buscando...", "Searching...") : tKey("BUSCAR")}
                </button>
              </div>
            </div>

            {searchResult ? (
              <div className="mt-5 rounded-md border border-[#8b6514] bg-[#120c02] p-4 shadow-[inset_0_0_18px_rgba(255,191,31,0.12)]">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <PlayerAvatar user={searchResult?.user || {}} />
                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => openPlayerProfile(searchResult?.user || {})}
                      className="truncate text-left text-base font-black text-[#fff7d1] transition hover:text-[#ffd45c]"
                    >
                      {searchResult?.user?.displayName || "-"}
                    </button>
                    <div className="truncate text-sm text-[#e7c979]">
                      {searchResult?.user?.email || "-"}
                    </div>
                    <div className="mt-1 text-xs font-semibold text-[#ffd45c]">
                      {t("Codigo", "Code")}: {searchResult?.user?.playerCode || "-"}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span
                      className={cx(
                        "inline-flex rounded-md border px-3 py-1 text-xs font-black uppercase",
                        statusClass[searchResult?.status] || statusClass.offline
                      )}
                    >
                      {statusLabel[searchResult?.status] || statusLabel.offline}
                    </span>
                    <span className="inline-flex rounded-md border border-[#8b6514] bg-[#261902] px-3 py-1 text-xs font-black uppercase text-[#fff2b6]">
                      {relationLabel[searchResult?.relation] || searchResult?.relation}
                    </span>
                  </div>
                </div>

                {searchResult?.relation === "none" ? (
                  <button
                    type="button"
                    onClick={() => handleSendRequest(searchResult?.user?.playerCode)}
                    className="mt-4 rounded-md border border-[#00d877] bg-[#053a2d] px-5 py-2 text-xs font-black uppercase text-[#67ffb2] transition hover:bg-[#07513d]"
                  >
                    {t("Enviar solicitud de amistad", "Send friend request")}
                  </button>
                ) : null}
              </div>
            ) : null}
          </article>

          <article className="rounded-md border border-[#ff3348] bg-[#21050a]/95 p-5 shadow-[0_0_26px_rgba(255,51,72,0.18)]">
            <div className="flex items-center gap-3">
              <FaUserFriends className="h-5 w-5 text-[#ff5d73]" />
              <h2 className="text-lg font-black uppercase text-[#ffe6ea]">{tKey("SOLICITUDES")}</h2>
            </div>

            <div className="mt-5">
              <div className="flex items-center gap-3">
                <h3 className="text-xs font-black uppercase text-[#ffd1d8]">
                  {tKey("RECIBIDAS")}
                </h3>
                <CountBadge tone="red">{socialCounts.incomingRequests}</CountBadge>
              </div>
              <div className="mt-3 space-y-3">
                {requests.incoming.length === 0 ? (
                  <div className="rounded-md border border-[#7b1724] bg-[#120307] px-4 py-3 text-sm text-[#f0a9b3]">
                    {t("No tienes solicitudes pendientes.", "No pending requests.")}
                  </div>
                ) : (
                  requests.incoming.map((request) => (
                    <div
                      key={request.id}
                      className="grid gap-3 rounded-md border border-[#7b1724] bg-[#120307] p-3 sm:grid-cols-[auto_1fr_auto]"
                    >
                      <PlayerAvatar user={request?.fromUser || {}} />
                      <div className="min-w-0">
                        <button
                          type="button"
                          onClick={() => openPlayerProfile(request?.fromUser || {})}
                          className="truncate text-left text-base font-black text-[#ffe6ea] transition hover:text-[#ff8d9c]"
                        >
                          {request?.fromUser?.displayName || "-"}
                        </button>
                        <div className="mt-1 text-sm font-medium text-[#f0a9b3]">
                          {t("Codigo", "Code")}: {request?.fromUser?.playerCode || "-"}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 sm:justify-end">
                        <span className="text-sm text-[#f0a9b3]">
                          {formatRelativeTime(request.createdAt, language) ||
                            t("Reciente", "Recent")}
                        </span>
                        <button
                          type="button"
                          aria-label={t("Aceptar solicitud", "Accept request")}
                          onClick={() => handleAcceptRequest(request.id)}
                          className="flex h-11 w-16 items-center justify-center rounded-md border border-[#00d877] bg-[#053a2d] text-[#67ffb2] transition hover:bg-[#07513d]"
                        >
                          <FaCheck className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          aria-label={t("Rechazar solicitud", "Decline request")}
                          onClick={() => handleDenyRequest(request.id)}
                          className="flex h-11 w-16 items-center justify-center rounded-md border border-[#ff3348] bg-[#3b0711] text-[#ff6374] transition hover:bg-[#52101a]"
                        >
                          <FaTimes className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="mt-5">
              <div className="flex items-center gap-3">
                <h3 className="text-xs font-black uppercase text-[#ffd1d8]">
                  {tKey("ENVIADAS")}
                </h3>
                <CountBadge tone="red">{socialCounts.outgoingRequests}</CountBadge>
              </div>
              <div className="mt-3 space-y-3">
                {requests.outgoing.length === 0 ? (
                  <div className="rounded-md border border-[#7b1724] bg-[#120307] px-4 py-3 text-sm text-[#f0a9b3]">
                    {t("No has enviado solicitudes pendientes.", "No outgoing pending requests.")}
                  </div>
                ) : (
                  requests.outgoing.map((request) => (
                    <div
                      key={request.id}
                      className="grid gap-3 rounded-md border border-[#7b1724] bg-[#120307] p-3 sm:grid-cols-[auto_1fr_auto]"
                    >
                      <PlayerAvatar user={request?.toUser || {}} />
                      <div className="min-w-0">
                        <button
                          type="button"
                          onClick={() => openPlayerProfile(request?.toUser || {})}
                          className="truncate text-left text-base font-black text-[#ffe6ea] transition hover:text-[#ff8d9c]"
                        >
                          {request?.toUser?.displayName || "-"}
                        </button>
                        <div className="mt-1 text-sm font-medium text-[#f0a9b3]">
                          {t("Codigo", "Code")}: {request?.toUser?.playerCode || "-"}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 sm:justify-end">
                        <span className="text-sm text-[#f0a9b3]">
                          {t("Pendiente", "Pending")}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleCancelRequest(request.id)}
                          className="min-h-[42px] rounded-md border border-[#ff8a17] bg-[#3b1f06] px-6 text-sm font-black uppercase text-[#ffa638] transition hover:bg-[#522b08]"
                        >
                          {t("Cancelar", "Cancel")}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </article>
        </section>

        <section className="rounded-md border border-[#0b4a8e] bg-[#020b20]/90 p-5 shadow-[0_0_20px_rgba(0,123,255,0.12)]">
          <div className="flex items-center gap-3">
            <FaUsers className="h-5 w-5 text-[#39a9ff]" />
            <h2 className="text-lg font-black uppercase text-white">
              {t("Tus amigos e invitaciones", "Your friends and invites")}
            </h2>
          </div>

          <div className="mt-5 grid gap-5 xl:grid-cols-[1.35fr_1fr]">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-xs font-black uppercase text-[#d8e8ff]">
                  {tKey("LISTA_DE_AMIGOS")}
                </h3>
                <CountBadge>{friends.length}</CountBadge>
              </div>
              <div className="mt-3 space-y-3">
                {friends.length === 0 ? (
                  <div className="rounded-md border border-[#17365e] bg-[#061735] px-4 py-3 text-sm text-[#9fb7d8]">
                    {t("Aun no tienes amigos agregados.", "You do not have friends yet.")}
                  </div>
                ) : (
                  visibleFriends.map((item) => {
                    const friend = item.user || {};
                    const friendPlayerCode = String(friend.playerCode || "").trim();
                    const draft = getInviteDraft(friendPlayerCode);
                    const canJoin =
                      item.status === "in_match" && Boolean(item.activeMatch?.spectatable);

                    return (
                      <div
                        key={item.friendshipId}
                        className="rounded-md border border-[#16477d] bg-[#061735] p-3 shadow-[inset_0_0_18px_rgba(26,128,255,0.08)]"
                      >
                        <div className="grid gap-3 lg:grid-cols-[auto_1fr_auto] lg:items-center">
                          <PlayerAvatar user={friend} />
                          <div className="min-w-0">
                            <button
                              type="button"
                              onClick={() => openPlayerProfile(friend)}
                              className="truncate text-left text-base font-black text-white transition hover:text-[#73cfff]"
                            >
                              {friend.displayName || "-"}
                            </button>
                            <div className="truncate text-sm font-medium text-[#a9bddc]">
                              {friend.email || "-"}
                            </div>
                            <div className="mt-1 text-sm font-medium text-[#b6d6ff]">
                              {t("Codigo", "Code")}: {friend.playerCode || "-"}
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-3 lg:justify-end">
                            <span
                              className={cx(
                                "inline-flex min-w-[112px] justify-center rounded-md border px-3 py-2 text-xs font-black uppercase",
                                statusClass[item.status] || statusClass.offline
                              )}
                            >
                              {statusLabel[item.status] || statusLabel.offline}
                            </span>
                            <button
                              type="button"
                              onClick={() => openPlayerProfile(friend)}
                              disabled={!buildProfileTargetFromUser(friend)}
                              className="min-h-[42px] min-w-[136px] rounded-md border border-[#167dff] bg-[#071735] px-4 text-xs font-black uppercase text-[#d9efff] transition hover:border-[#2aa6ff] hover:text-[#58bbff] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {t("Ver perfil", "View profile")}
                            </button>
                          </div>
                        </div>

                        {canJoin ? (
                          <button
                            type="button"
                            onClick={() => handleJoinFriend(friendPlayerCode)}
                            disabled={!friendPlayerCode}
                            className="mt-3 min-h-[42px] w-full rounded-md border border-[#00d877] bg-[#053a2d] px-5 text-sm font-black uppercase text-[#67ffb2] transition hover:bg-[#07513d] disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {tKey("JOIN")}
                          </button>
                        ) : (
                          <div className="mt-3 grid gap-3 md:grid-cols-[minmax(120px,180px)_minmax(90px,120px)_1fr]">
                            <select
                              value={draft.mode}
                              onChange={(event) =>
                                setInviteDraftField(friendPlayerCode, "mode", event.target.value)
                              }
                              className="min-h-[38px] rounded-md border border-[#173f76] bg-[#061329] px-3 text-xs font-black uppercase text-[#d6e9ff] outline-none focus:border-[#18aaff]"
                            >
                              {MODE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option[language] || option.en || option.es}
                                </option>
                              ))}
                            </select>

                            <select
                              value={draft.havingTime}
                              onChange={(event) =>
                                setInviteDraftField(
                                  friendPlayerCode,
                                  "havingTime",
                                  Number(event.target.value)
                                )
                              }
                              className="min-h-[38px] rounded-md border border-[#173f76] bg-[#061329] px-3 text-xs font-black uppercase text-[#d6e9ff] outline-none focus:border-[#18aaff]"
                            >
                              {TIME_OPTIONS.map((time) => (
                                <option key={time} value={time}>
                                  {time} min
                                </option>
                              ))}
                            </select>

                            <button
                              type="button"
                              onClick={() => handleSendInvite(friendPlayerCode)}
                              disabled={!friendPlayerCode}
                              className="min-h-[38px] rounded-md border border-[#00d877] bg-[#053a2d] px-5 text-xs font-black uppercase text-[#67ffb2] transition hover:bg-[#07513d] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {t("Invitar", "Invite")}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {friends.length > PREVIEW_SOCIAL_ITEMS ? (
                <button
                  type="button"
                  onClick={() => setShowAllFriends((prev) => !prev)}
                  className="mt-4 flex min-h-[48px] w-full items-center justify-center gap-4 rounded-md border border-[#16477d] bg-[#061735] text-sm font-black uppercase text-[#47b7ff] transition hover:border-[#2aa6ff] hover:bg-[#08214a]"
                >
                  <FaUsers className="h-5 w-5" />
                  <span>
                    {showAllFriends
                      ? t("Mostrar menos amigos", "Show fewer friends")
                      : t("Ver todos los amigos", "View all friends")}
                  </span>
                </button>
              ) : null}
            </div>

            <div className="space-y-0 overflow-hidden rounded-md border border-[#16477d] bg-[#061329]">
              <article id="xima-friends-invites-received" className="p-4">
                <div className="flex items-center gap-3">
                  <h3 className="text-sm font-black uppercase text-white">
                    {tKey("INVITACIONES_RECIBIDAS")}
                  </h3>
                  <CountBadge>{socialCounts.incomingInvites}</CountBadge>
                </div>
                <div className="mt-4 space-y-3">
                  {invites.incoming.length === 0 ? (
                    <div className="rounded-md border border-[#17365e] bg-[#020b20] px-4 py-3 text-sm text-[#9fb7d8]">
                      {t("No tienes invitaciones pendientes.", "No pending invites.")}
                    </div>
                  ) : (
                    visibleIncomingInvites.map((invite) => (
                      <div
                        key={invite.id}
                        className="grid gap-3 rounded-md border border-[#16477d] bg-[#061735] p-3 lg:grid-cols-[auto_1fr_auto]"
                      >
                        <PlayerAvatar user={invite?.fromUser || {}} />
                        <div className="min-w-0">
                          <button
                            type="button"
                            onClick={() => openPlayerProfile(invite?.fromUser || {})}
                            className="truncate text-left text-base font-black text-white transition hover:text-[#73cfff]"
                          >
                            {invite?.fromUser?.displayName || "-"}
                          </button>
                          <div className="mt-1 text-sm text-[#a9bddc]">
                            {t("Te ha enviado una invitacion", "Sent you an invite")}
                          </div>
                          <div className="mt-1 text-xs font-semibold text-[#9fd8ff]">
                            {tKey("MODE")}: {String(invite.mode || "").toUpperCase()} /{" "}
                            {invite.havingTime} min
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 lg:justify-end">
                          <span className="text-sm text-[#9fb0d0]">
                            {formatRelativeTime(invite.createdAt, language) ||
                              t("Reciente", "Recent")}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleAcceptInvite(invite.id)}
                            className="min-h-[42px] min-w-[132px] rounded-md border border-[#00d877] bg-[#053a2d] px-5 text-xs font-black uppercase text-[#67ffb2] transition hover:bg-[#07513d]"
                          >
                            {t("Aceptar", "Accept")}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDenyInvite(invite.id)}
                            className="min-h-[42px] min-w-[132px] rounded-md border border-[#ff3348] bg-[#3b0711] px-5 text-xs font-black uppercase text-[#ff6374] transition hover:bg-[#52101a]"
                          >
                            {t("Rechazar", "Decline")}
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {invites.incoming.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setShowAllIncomingInvites((prev) => !prev)}
                    className="mt-4 flex min-h-[44px] w-full items-center justify-center gap-4 border-t border-[#16477d] pt-4 text-sm font-black uppercase text-[#47b7ff] transition hover:text-[#7ed0ff]"
                  >
                    <span>
                      {showAllIncomingInvites
                        ? t("Mostrar menos invitaciones recibidas", "Show fewer incoming invites")
                        : t(
                            "Ver todas las invitaciones recibidas",
                            "View all incoming invites"
                          )}
                    </span>
                    <FaArrowRight className="h-4 w-4" />
                  </button>
                ) : null}
              </article>

              <article
                id="xima-friends-invites-sent"
                className="border-t border-[#16477d] p-4"
              >
                <div className="flex items-center gap-3">
                  <h3 className="text-sm font-black uppercase text-white">
                    {tKey("INVITACIONES_ENVIADAS")}
                  </h3>
                  <CountBadge>{socialCounts.outgoingInvites}</CountBadge>
                </div>
                <div className="mt-4 space-y-3">
                  {invites.outgoing.length === 0 ? (
                    <div className="rounded-md border border-[#17365e] bg-[#020b20] px-4 py-3 text-sm text-[#9fb7d8]">
                      {t("No has enviado invitaciones.", "No invites sent yet.")}
                    </div>
                  ) : (
                    visibleOutgoingInvites.map((invite) => (
                      <div
                        key={invite.id}
                        className="grid gap-3 rounded-md border border-[#16477d] bg-[#061735] p-3 lg:grid-cols-[auto_1fr_auto]"
                      >
                        <PlayerAvatar user={invite?.toUser || {}} />
                        <div className="min-w-0">
                          <button
                            type="button"
                            onClick={() => openPlayerProfile(invite?.toUser || {})}
                            className="truncate text-left text-base font-black text-white transition hover:text-[#73cfff]"
                          >
                            {invite?.toUser?.displayName || "-"}
                          </button>
                          <div className="mt-1 text-sm text-[#a9bddc]">
                            {t("Invitacion enviada", "Invite sent")}
                          </div>
                          <div className="mt-1 text-xs font-semibold text-[#9fd8ff]">
                            {formatDate(invite.createdAt, language)}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 lg:justify-end">
                          <span className="text-sm text-[#9fb0d0]">
                            {String(invite.status || "").toLowerCase() === "pending"
                              ? t("Pendiente", "Pending")
                              : String(invite.status || "").toUpperCase()}
                          </span>
                          {invite.status === "pending" ? (
                            <button
                              type="button"
                              onClick={() => handleCancelInvite(invite.id)}
                              className="min-h-[42px] min-w-[132px] rounded-md border border-[#ff8a17] bg-[#3b1f06] px-5 text-xs font-black uppercase text-[#ffa638] transition hover:bg-[#522b08]"
                            >
                              {t("Cancelar", "Cancel")}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {invites.outgoing.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setShowAllOutgoingInvites((prev) => !prev)}
                    className="mt-4 flex min-h-[44px] w-full items-center justify-center gap-4 border-t border-[#16477d] pt-4 text-sm font-black uppercase text-[#47b7ff] transition hover:text-[#7ed0ff]"
                  >
                    <span>
                      {showAllOutgoingInvites
                        ? t("Mostrar menos invitaciones enviadas", "Show fewer outgoing invites")
                        : t("Ver todas las invitaciones enviadas", "View all outgoing invites")}
                    </span>
                    <FaArrowRight className="h-4 w-4" />
                  </button>
                ) : null}
              </article>
            </div>
          </div>
        </section>

        {loading ? (
          <div className="rounded-md border border-[#17365e] bg-[#061735] px-4 py-3 text-sm font-semibold text-[#9fb7d8]">
            {t("Cargando...", "Loading...")}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Friends;
